import React, { useState, useEffect, useMemo } from 'react';
import { useSession } from '../../../Utils/SessionContext';
import { API_ENDPOINTS } from '../../../Utils/ApiConfig';
import { usePopupClose } from '../../../Utils/usePopupClose';
import useSkillsAutocomplete from '../../../Utils/useSkillsAutocomplete';
import ComboBoxSelect from '../../../CreateEvaluationComponents/Shards/ComboBoxSelect';
import InputList from '../../../CreateEvaluationComponents/Shards/InputList';
import CodeEditor from '../../Utils/CodeEditor';
import { updateChallengeQuestions } from '../../Utils/QuestionUpdater';
import { 
  createQuestionFromTemplate,
  questionTypeRequiresCodeSnippet,
  questionTypeRequiresOptions,
  questionTypeRequiresStatement,
  getAllMainCategories,
  getQuestionTypesForMainCategory,
  getQuestionTypeLabel
} from '../../Utils/QuestionTemplates';
import GenerateQuestionPanel from './Shards/GenerateQuestionPanel';
import toast from 'react-hot-toast';

/**
 * AddChallengeQuestion Component
 * Modal for adding new challenge questions
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Close handler
 * @param {string} challengeId - Challenge ID for API call
 * @param {string} evaluationType - The type of evaluation (e.g., 'job', 'talent')
 * @param {Object} roleCategory - The role category object from challenge
 * @param {array} allQuestions - Full questions array
 * @param {function} onSuccess - Callback after successful save
 */
const AddChallengeQuestion = ({
  isOpen,
  onClose,
  challengeId,
  evaluationType,
  roleCategory,
  allQuestions,
  onSuccess
}) => {
  const { user } = useSession();
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratePanelOpen, setIsGeneratePanelOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [selectedMainCategory, setSelectedMainCategory] = useState('');
  
  // Skills autocomplete hook
  const { skillsOptions, isLoadingSkills, searchSkills, clearSkills } = useSkillsAutocomplete();
  
  // Use popup close hook - close on ESC key only, not on click outside
  const popupRef = usePopupClose(isOpen, onClose, false, true);

  // Handle mounting/unmounting with animation
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      // Small delay to ensure DOM is ready for animation
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      // Wait for animation to complete before unmounting
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);
  
  // State for new question
  const [newQuestion, setNewQuestion] = useState({
    questionText: '',
    questionDetails: '',
    skillsTested: [],
    options: [],
    type: '',
    codeSnippet: '',
    statement: '',
    breadth: 'Involved',
    seniority: 'Senior',
    maxTime: '10',
    furtherGuidance: '',
    assessmentInstructions: ''
  });

  // Reset form when modal opens and auto-detect category
  useEffect(() => {
    if (isOpen) {
      // Auto-detect main category from roleCategory
      const defaultCategory = roleCategory?.mainCategory || '';
      setSelectedMainCategory(defaultCategory);
      
      setNewQuestion({
        questionText: '',
        questionDetails: '',
        skillsTested: [],
        options: [],
        type: '',
        codeSnippet: '',
        statement: '',
        breadth: 'Involved',
        seniority: 'Senior',
        maxTime: '10',
        furtherGuidance: '',
        assessmentInstructions: ''
      });
      setIsGeneratePanelOpen(false);
    }
  }, [isOpen, roleCategory]);

  // Update maxTime when breadth changes
  useEffect(() => {
    const breadthToTime = {
      'Focused': '5',
      'Involved': '10',
      'Comprehensive': '20'
    };
    setNewQuestion(prev => ({
      ...prev,
      maxTime: breadthToTime[prev.breadth] || '10'
    }));
  }, [newQuestion.breadth]);

  // Helper function to generate next question ID
  const getNextQuestionId = () => {
    if (!allQuestions || allQuestions.length === 0) return 1;
    const maxId = Math.max(...allQuestions.map(q => parseInt(q.id) || 0));
    return maxId + 1;
  };

  // Helper function to generate UUID
  const generateUUID = () => {
    return crypto.randomUUID();
  };

  // Get available question types for the selected category
  const availableQuestionTypes = useMemo(() => {
    if (!selectedMainCategory) return [];
    return getQuestionTypesForMainCategory(selectedMainCategory);
  }, [selectedMainCategory]);

  // Check if form is valid for submission
  const isFormValid = useMemo(() => {
    // Main category and type required
    if (!selectedMainCategory || !newQuestion.type) return false;
    
    // Skills required
    if (!newQuestion.skillsTested || newQuestion.skillsTested.length === 0) return false;
    
    // Question text required
    if (!newQuestion.questionText.trim()) return false;
    
    // Code snippet required for code questions
    if (questionTypeRequiresCodeSnippet(newQuestion.type) && !newQuestion.codeSnippet.trim()) {
      return false;
    }
    
    // Options required for choice questions (minimum 2)
    if (questionTypeRequiresOptions(newQuestion.type, 'challenge')) {
      if (!newQuestion.options || newQuestion.options.length < 2) return false;
    }
    
    // Statement required for true-false
    if (questionTypeRequiresStatement(newQuestion.type) && !newQuestion.statement.trim()) {
      return false;
    }
    
    return true;
  }, [selectedMainCategory, newQuestion]);

  // Convert skills array to ComboBoxSelect format
  const skillsAsOptions = newQuestion.skillsTested.map(skill => ({
    value: skill,
    label: skill
  }));

  // Convert options array to InputList format
  const optionsAsItems = newQuestion.options.map(opt => ({
    title: opt,
    subtitle: ''
  }));

  // Handle question type change
  const handleTypeChange = (e) => {
    setNewQuestion({ ...newQuestion, type: e.target.value });
  };

  // Handle skills change
  const handleSkillsChange = (selectedOptions) => {
    const skills = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
    setNewQuestion({ ...newQuestion, skillsTested: skills });
  };

  // Handle creating new skill
  const handleCreateSkill = (inputValue) => {
    const newSkills = [...newQuestion.skillsTested, inputValue];
    setNewQuestion({ ...newQuestion, skillsTested: newSkills });
  };

  // Handle options change
  const handleOptionsChange = (items) => {
    const options = items.map(item => item.title);
    setNewQuestion({ ...newQuestion, options });
  };

  // Handle question text change
  const handleQuestionTextChange = (e) => {
    setNewQuestion({ ...newQuestion, questionText: e.target.value });
  };

  // Handle question details change
  const handleQuestionDetailsChange = (e) => {
    setNewQuestion({ ...newQuestion, questionDetails: e.target.value });
  };

  // Handle code snippet change
  const handleCodeSnippetChange = (value) => {
    setNewQuestion({ ...newQuestion, codeSnippet: value });
  };

  // Handle statement change (for true-false)
  const handleStatementChange = (e) => {
    setNewQuestion({ ...newQuestion, statement: e.target.value });
  };

  // Handle assessment instructions change
  const handleAssessmentInstructionsChange = (e) => {
    setNewQuestion({ ...newQuestion, assessmentInstructions: e.target.value });
  };

  // Handle generate button click
  const handleGenerate = async () => {
    // Validate required fields before generating
    if (!newQuestion.type) {
      toast.error('Please select a question type first');
      return;
    }

    if (!newQuestion.skillsTested || newQuestion.skillsTested.length === 0) {
      toast.error('Please select at least one skill to test');
      return;
    }

    setIsGenerating(true);
    setIsGeneratePanelOpen(false); // Close the panel when generation starts

    try {
      const response = await fetch(API_ENDPOINTS.GENERATE_CHALLENGE_QUESTION, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.access_token}`
        },
        body: JSON.stringify({
          question_type: newQuestion.type,
          type: evaluationType,
          skills_tested: newQuestion.skillsTested,
          challenge_id: challengeId,
          role_category: roleCategory,
          breadth: newQuestion.breadth,
          seniority: newQuestion.seniority,
          max_time: newQuestion.maxTime,
          further_guidance: newQuestion.furtherGuidance
        })
      });

      if (!response.ok) {
        const error = new Error(`Error: ${response.status} ${response.statusText}`);
        error.status = response.status;
        throw error;
      }

      const data = await response.json();
      console.log('Generated question data:', data);

      // Extract question from array response
      const generatedQuestion = data[0].question;

      // Update form with generated data
      setNewQuestion(prev => ({
        ...prev,
        questionText: generatedQuestion.questionText || prev.questionText,
        questionDetails: generatedQuestion.questionDetails || prev.questionDetails,
        codeSnippet: generatedQuestion.codeSnippet || prev.codeSnippet,
        statement: generatedQuestion.statement || prev.statement,
        options: generatedQuestion.options || prev.options,
        assessmentInstructions: generatedQuestion.assessment_instructions || prev.assessmentInstructions
      }));

      toast.success('Question generated successfully!');
    } catch (error) {
      console.error('Error generating question:', error);
      toast.error('Failed to generate question. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Validate form
  const validateForm = () => {
    if (!newQuestion.type) {
      toast.error('Please select a question type');
      return false;
    }

    if (!newQuestion.questionText.trim()) {
      toast.error('Question text is required');
      return false;
    }

    if (!newQuestion.skillsTested || newQuestion.skillsTested.length === 0) {
      toast.error('At least one skill must be selected');
      return false;
    }

    // Validate code snippet for code questions using centralized helper
    if (questionTypeRequiresCodeSnippet(newQuestion.type) && !newQuestion.codeSnippet.trim()) {
      toast.error('Code snippet is required for this question type');
      return false;
    }

    // Validate options for choice questions using centralized helper
    if (questionTypeRequiresOptions(newQuestion.type, 'challenge') && (!newQuestion.options || newQuestion.options.length < 2)) {
      toast.error('Choice questions must have at least 2 options');
      return false;
    }

    // Validate statement for true-false using centralized helper
    if (questionTypeRequiresStatement(newQuestion.type) && !newQuestion.statement.trim()) {
      toast.error('Statement is required for true-false questions');
      return false;
    }

    return true;
  };

  // Handle save
  const handleSave = async () => {
    if (!validateForm()) return;

    setIsSaving(true);

    try {
      // Create question from template with user's input
      const questionToAdd = createQuestionFromTemplate(
        newQuestion.type,
        'challenge',
        {
          id: getNextQuestionId(),
          uuid: generateUUID(),
          questionText: newQuestion.questionText,
          questionDetails: newQuestion.questionDetails,
          skillsTested: newQuestion.skillsTested,
          breadth: newQuestion.breadth,
          seniority: newQuestion.seniority,
          timeToSolve: newQuestion.maxTime,
          // AI assessment guidance field
          ...(newQuestion.assessmentInstructions && {
            assessmentInstructions: newQuestion.assessmentInstructions,
          }),
          // Type-specific overrides using centralized helpers
          ...(questionTypeRequiresCodeSnippet(newQuestion.type) && {
            codeSnippet: newQuestion.codeSnippet,
          }),
          ...(questionTypeRequiresOptions(newQuestion.type, 'challenge') && {
            options: newQuestion.options,
          }),
          ...(questionTypeRequiresStatement(newQuestion.type) && {
            statement: newQuestion.statement,
          }),
        }
      );

      // Add to questions array
      const updatedQuestions = [...allQuestions, questionToAdd];

      // Use utility function to update questions
      await updateChallengeQuestions(challengeId, updatedQuestions, user);

      toast.success('Question added successfully!');
      
      // Close modal and trigger success callback
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error adding question:', error);
      toast.error('Failed to add question. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };


  if (!shouldRender) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Overlay with blur */}
      <div 
        className={`fixed inset-0 bg-white transition-all duration-300 ${
          isAnimating ? 'backdrop-blur-sm bg-opacity-40' : 'bg-opacity-0'
        }`}
        onClick={onClose}
      />
      
      {/* Sliding Panel */}
      <div className="fixed inset-y-0 right-0 flex max-w-full">
        <div 
          ref={popupRef}
          className={`w-screen max-w-4xl transform transition-all duration-300 ease-in-out ${
            isAnimating ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="h-full flex flex-col bg-white shadow-2xl overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-20">
            <h2 className="text-2xl font-bold text-gray-900">Add New Challenge Question</h2>
            <button 
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-8 space-y-8 overflow-visible">
            {/* Step 1 & 2: Main Category and Question Type in 2 columns */}
            <div className="grid grid-cols-2 gap-6">
              {/* Main Category Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Role Category <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedMainCategory}
                  onChange={(e) => {
                    setSelectedMainCategory(e.target.value);
                    // Reset question type when category changes
                    setNewQuestion(prev => ({ ...prev, type: '' }));
                  }}
                  className="select-custom w-full py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                >
                  <option value="">Select a category...</option>
                  {getAllMainCategories().map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              {/* Question Type Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Question Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={newQuestion.type}
                  onChange={handleTypeChange}
                  disabled={!selectedMainCategory}
                  className="select-custom w-full py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">Select a question type...</option>
                  {availableQuestionTypes.map(type => (
                    <option key={type} value={type}>
                      {getQuestionTypeLabel(type)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Step 3: Show Skills Tested after type is selected */}
            {selectedMainCategory && newQuestion.type && (
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Skills Tested <span className="text-red-500">*</span>
                </label>
                <ComboBoxSelect
                  value={skillsAsOptions}
                  onChange={handleSkillsChange}
                  options={skillsOptions}
                  placeholder="Type to search or add custom skills..."
                  onInputChange={(inputValue, action) => {
                    if (action.action === 'input-change') {
                      if (inputValue && inputValue.length >= 2) {
                        searchSkills(inputValue);
                      } else {
                        clearSkills();
                      }
                    }
                  }}
                  isLoading={isLoadingSkills}
                  onCreateOption={handleCreateSkill}
                  isMulti={true}
                />
              </div>
            )}

            {/* Step 4: Show Question Content after skills are added */}
            {selectedMainCategory && newQuestion.type && newQuestion.skillsTested.length > 0 && (
              <>
                {/* Question Content Box */}
                <div className="space-y-6 relative">
                  {/* Loading Overlay - stays under sticky header/footer */}
                  {isGenerating && (
                    <div className="absolute inset-0 bg-white bg-opacity-95 backdrop-blur-sm rounded-lg flex items-center justify-center z-10">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
                        <p className="text-lg font-semibold text-gray-900">Generating Question...</p>
                        <p className="text-sm text-gray-600 mt-2">This may take a moment</p>
                      </div>
                    </div>
                  )}

                  <div className="border-gray-300 pb-2 mb-4 flex justify-between items-center relative">
                    <h3 className="text-lg font-semibold text-gray-900">Question Content</h3>
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setIsGeneratePanelOpen(!isGeneratePanelOpen)}
                        disabled={!newQuestion.type || !newQuestion.skillsTested.length}
                        className="px-6 py-2.5 text-white font-semibold rounded-lg hover:opacity-90 transition-colors flex items-center gap-2 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ backgroundColor: 'var(--color-brand-blue)' }}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        Generate Question
                        <svg 
                          className={`w-4 h-4 transition-transform ${isGeneratePanelOpen ? 'rotate-180' : ''}`} 
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>

                      {/* Floating Generate Panel */}
                      <GenerateQuestionPanel
                        isOpen={isGeneratePanelOpen}
                        onClose={() => setIsGeneratePanelOpen(false)}
                        questionData={newQuestion}
                        onQuestionDataChange={setNewQuestion}
                        onGenerate={handleGenerate}
                        isGenerating={isGenerating}
                      />
                    </div>
                  </div>

                  {/* Question Text */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-3">
                      Question Text <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={newQuestion.questionText}
                      onChange={handleQuestionTextChange}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base bg-white"
                      placeholder="Enter the question text..."
                    />
                  </div>

                  {/* Question Details */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-3">
                      Question Details
                    </label>
                    <textarea
                      value={newQuestion.questionDetails}
                      onChange={handleQuestionDetailsChange}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base bg-white"
                      placeholder="Enter additional details, constraints, or requirements for this question..."
                    />
                  </div>

                  {/* Code Snippet - Only for code questions */}
                  {questionTypeRequiresCodeSnippet(newQuestion.type) && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-3">
                        Code Snippet <span className="text-red-500">*</span>
                      </label>
                      <CodeEditor
                        question={{ skillsTested: newQuestion.skillsTested, id: 'new' }}
                        value={newQuestion.codeSnippet}
                        onChange={handleCodeSnippetChange}
                        label="Syntax: "
                      />
                    </div>
                  )}

                  {/* Statement - Only for true-false */}
                  {questionTypeRequiresStatement(newQuestion.type) && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-900 mb-3">
                        Statement <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={newQuestion.statement}
                        onChange={handleStatementChange}
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base bg-white"
                        placeholder="Enter the true/false statement..."
                      />
                    </div>
                  )}

                  {/* Options - Only for choice questions */}
                  {questionTypeRequiresOptions(newQuestion.type, 'challenge') && (
                    <div>
                      <InputList
                        label="Options (minimum 2 required)"
                        items={optionsAsItems}
                        onItemsChange={handleOptionsChange}
                        placeholder="Enter an option..."
                      />
                    </div>
                  )}
                </div>

                {/* AI Assessment Guidance Section */}
                <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-6 space-y-6">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                      <h3 className="text-lg font-semibold text-purple-900">Custom AI Assessment Guidance</h3>
                    </div>
                    <span className="text-xs text-purple-600 font-medium">(optional)</span>
                  </div>
                  <p className="text-sm text-purple-700 mb-4">
                    Provide instructions to guide the AI when evaluating candidate responses. Include the ideal solution, assessment criteria, and any context about what to focus on or ignore during evaluation.
                  </p>

                  {/* Assessment Context - For all questions */}
                  <div>
                    <textarea
                      value={newQuestion.assessmentInstructions}
                      onChange={handleAssessmentInstructionsChange}
                      rows={6}
                      className="w-full px-4 py-3 border border-purple-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-base bg-white"
                      placeholder="e.g., Focus primarily on the logic. It is okay if the syntax is slightly off or if they miss a semicolon, as long as the refactoring pattern is correct."
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-center gap-4 z-20">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="px-8 py-3 bg-white text-gray-700 border-2 border-gray-300 font-semibold rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-lg flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              CANCEL
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || !isFormValid}
              className="px-8 py-3 bg-[#33C6B6] text-white font-semibold rounded-lg hover:bg-[#2db5a6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-lg flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  SAVING...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  ADD QUESTION
                </>
              )}
            </button>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddChallengeQuestion;
